/**
 * MonitoringCenter Component
 *
 * Main dashboard card containing tabs for alerts, products, and history.
 * Handles search, filters, tab content, and actions menu orchestration.
 */

import React, { type RefObject } from 'react';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Box,
  Icon,
  Button,
  Tabs,
  Popover,
  ActionList,
} from '@shopify/polaris';
import {
  TargetIcon,
  MenuHorizontalIcon,
  RefreshIcon,
  ExportIcon,
  PlusIcon,
} from '@shopify/polaris-icons';
import { useRevalidator } from 'react-router';

import {
  SearchFilterOverlay,
  AppliedFilterPills,
  ActiveAlertsTabContent,
  MonitoredProductsTabContent,
  AlertHistoryTabContent,
} from './index';
import { AsyncSection, SkeletonTable } from '../loading';

import type { EnhancedAlert, TrackedProduct, OnboardingData, AlertSeverity } from '../../types/dashboard';
import type { AppliedFilter } from '../../types/search';

// Re-export tab type for consumers
export interface TabDescriptor {
  id: string;
  content: string;
  badge?: string;
  accessibilityLabel?: string;
}

export interface MonitoringCenterProps {
  // Ref for scroll targeting
  scrollRef: RefObject<HTMLDivElement | null>;

  // Tab state
  selectedTab: number;
  tabs: TabDescriptor[];
  isTabSwitching: boolean;
  onTabChange: (tabIndex: number) => void;

  // Search state
  isSearchExpanded: boolean;
  onSearchExpandChange: (expanded: boolean) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  autocompleteSuggestions: Array<{ label: string; value: string }>;

  // Alert filters (matching SearchFilterOverlayProps types)
  severityFilter: AlertSeverity[];
  onSeverityChange: (values: AlertSeverity[]) => void;
  daysMin: string;
  daysMax: string;
  onDaysMinChange: (value: string) => void;
  onDaysMaxChange: (value: string) => void;
  daysRangeError: boolean;
  inventoryMin: string;
  inventoryMax: string;
  onInventoryMinChange: (value: string) => void;
  onInventoryMaxChange: (value: string) => void;
  inventoryRangeError: boolean;

  // Product filters (matching SearchFilterOverlayProps types)
  statusFilter: string[];
  onStatusChange: (values: string[]) => void;
  productTypeFilter: string[];
  onProductTypeChange: (values: string[]) => void;
  productVendorFilter: string[];
  onProductVendorChange: (values: string[]) => void;
  productTypeOptions: string[];
  productVendorOptions: string[];

  // Filter actions
  onClearAllFilters: () => void;
  totalFilterCount: number;
  appliedFilterPills: Array<{ key: string; label: string }>;
  onRemoveFilterPill: (key: string) => void;

  // Actions menu
  isActionsMenuOpen: boolean;
  onActionsMenuToggle: () => void;
  onActionsMenuClose: () => void;
  isRefreshing: boolean;
  onRefresh: () => void;
  onExport: () => void;
  isExporting: boolean;
  onOpenWizard: () => void;

  // Active Alerts tab data
  filteredAlerts: EnhancedAlert[];
  activeAlertsCount: number;
  appliedAlertFilters: AppliedFilter[];
  dismissingAlerts: Set<number>;
  resolvingAlerts: Set<number>;
  notifyingAlerts: Set<number>;
  onResolve: (alert: EnhancedAlert) => void;
  onDismiss: (alert: EnhancedAlert) => void;
  onNotify: (alert: EnhancedAlert) => void;
  onAlertClick: (alert: EnhancedAlert) => void;
  hasNotificationChannels: boolean;
  onAlertFiltersClearAll: () => void;
  onPopulateTestData: () => void;
  isGeneratingTestData: boolean;

  // Monitored Products tab data
  filteredProducts: TrackedProduct[];
  onboardingData: OnboardingData | null;
  shopDomain: string;
  onProductClick: (product: TrackedProduct) => void;
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;

  // Alert History tab data
  filteredHistory: EnhancedAlert[];

  // Test data state
  hasTestData?: boolean;
}

/**
 * Main monitoring center card with tabs and actions
 */
export function MonitoringCenter({
  scrollRef,
  selectedTab,
  tabs,
  isTabSwitching,
  onTabChange,
  isSearchExpanded,
  onSearchExpandChange,
  searchQuery,
  onSearchChange,
  autocompleteSuggestions,
  severityFilter,
  onSeverityChange,
  daysMin,
  daysMax,
  onDaysMinChange,
  onDaysMaxChange,
  daysRangeError,
  inventoryMin,
  inventoryMax,
  onInventoryMinChange,
  onInventoryMaxChange,
  inventoryRangeError,
  statusFilter,
  onStatusChange,
  productTypeFilter,
  onProductTypeChange,
  productVendorFilter,
  onProductVendorChange,
  productTypeOptions,
  productVendorOptions,
  onClearAllFilters,
  totalFilterCount,
  appliedFilterPills,
  onRemoveFilterPill,
  isActionsMenuOpen,
  onActionsMenuToggle,
  onActionsMenuClose,
  isRefreshing,
  onRefresh,
  onExport,
  isExporting,
  onOpenWizard,
  filteredAlerts,
  activeAlertsCount,
  appliedAlertFilters,
  dismissingAlerts,
  resolvingAlerts,
  notifyingAlerts,
  onResolve,
  onDismiss,
  onNotify,
  onAlertClick,
  hasNotificationChannels,
  onAlertFiltersClearAll,
  onPopulateTestData,
  isGeneratingTestData,
  filteredProducts,
  onboardingData,
  shopDomain,
  onProductClick,
  currentPage,
  totalPages,
  itemsPerPage,
  onPageChange,
  filteredHistory,
  hasTestData,
}: MonitoringCenterProps) {
  const revalidator = useRevalidator();

  return (
    <div className="monitoring-center-card" style={{ animationDelay: '100ms' }} ref={scrollRef as React.RefObject<HTMLDivElement>}>
      <Card>
        <BlockStack gap="400">
          {/* Header Row: Title + Search Trigger + Action Buttons */}
          <div className={`monitoring-header ${isSearchExpanded ? 'monitoring-header--search-expanded' : ''}`}>
            {/* Left side: Icon and Title */}
            <div className="monitoring-header__title">
              <InlineStack align="space-between" blockAlign="center">
                <InlineStack gap="200" blockAlign="center">
                  <Icon source={TargetIcon} tone="subdued" />
                  <BlockStack gap="100">
                    <Text as="h2" variant="headingMd" fontWeight="semibold">Monitoring Center</Text>
                    <Text as="p" variant="bodySm" tone="subdued">Track inventory alerts, products, and historical data</Text>
                  </BlockStack>
                </InlineStack>
              </InlineStack>
            </div>

            {/* Right side: Search Trigger + Actions Menu */}
            <div className="monitoring-header__actions">
              {/* Search and Filter Overlay */}
              <div className={`search-filter-wrapper ${isSearchExpanded ? 'search-filter-wrapper--expanded' : ''}`}>
                <SearchFilterOverlay
                  onExpandChange={onSearchExpandChange}
                  searchQuery={searchQuery}
                  onSearchChange={onSearchChange}
                  placeholder="Search products, SKUs..."
                  suggestions={autocompleteSuggestions}
                  // Alert filters
                  severityFilter={severityFilter}
                  onSeverityChange={onSeverityChange}
                  daysMin={daysMin}
                  daysMax={daysMax}
                  onDaysMinChange={onDaysMinChange}
                  onDaysMaxChange={onDaysMaxChange}
                  daysRangeError={daysRangeError}
                  inventoryMin={inventoryMin}
                  inventoryMax={inventoryMax}
                  onInventoryMinChange={onInventoryMinChange}
                  onInventoryMaxChange={onInventoryMaxChange}
                  inventoryRangeError={inventoryRangeError}
                  // Product filters
                  statusFilter={statusFilter}
                  onStatusChange={onStatusChange}
                  productTypeFilter={productTypeFilter}
                  onProductTypeChange={onProductTypeChange}
                  productVendorFilter={productVendorFilter}
                  onProductVendorChange={onProductVendorChange}
                  productTypeOptions={productTypeOptions}
                  productVendorOptions={productVendorOptions}
                  // Clear and counts
                  onClearAllFilters={onClearAllFilters}
                  totalFilterCount={totalFilterCount}
                />
              </div>

              {/* Actions Menu (context menu) */}
              <Popover
                active={isActionsMenuOpen}
                activator={
                  <Button
                    icon={MenuHorizontalIcon}
                    onClick={onActionsMenuToggle}
                    accessibilityLabel="More actions"
                  />
                }
                onClose={onActionsMenuClose}
                preferredAlignment="right"
              >
                <ActionList
                  items={[
                    {
                      content: 'Refresh data',
                      icon: RefreshIcon,
                      onAction: () => {
                        onActionsMenuClose();
                        onRefresh();
                      },
                      disabled: isRefreshing,
                    },
                    {
                      content: 'Export alerts',
                      icon: ExportIcon,
                      onAction: () => {
                        onActionsMenuClose();
                        onExport();
                      },
                      disabled: isExporting,
                    },
                    {
                      content: 'Setup wizard',
                      icon: PlusIcon,
                      onAction: () => {
                        onActionsMenuClose();
                        onOpenWizard();
                      },
                    },
                  ]}
                />
              </Popover>
            </div>
          </div>

          {/* Applied Filter Pills */}
          {appliedFilterPills.length > 0 && (
            <AppliedFilterPills
              filters={appliedFilterPills}
              onRemove={onRemoveFilterPill}
            />
          )}

          {/* Tabs */}
          <Tabs tabs={tabs} selected={selectedTab} onSelect={onTabChange} />

          {/* Tab Content */}
          <Box paddingBlockStart="200">
            <AsyncSection
              isLoading={isTabSwitching}
              isRefreshing={isRefreshing && revalidator.state === 'loading'}
              skeleton={<SkeletonTable rows={5} />}
              minHeight="160px"
              refreshMessage="Refreshing data..."
            >
              <div className="tab-container">
                {/* Active Alerts Tab */}
                <div className={`tab-content ${selectedTab === 0 ? 'tab-active' : ''}`}>
                  <ActiveAlertsTabContent
                    filteredAlerts={filteredAlerts}
                    activeAlertsCount={activeAlertsCount}
                    searchQuery={searchQuery}
                    appliedAlertFilters={appliedAlertFilters}
                    dismissingAlerts={dismissingAlerts}
                    resolvingAlerts={resolvingAlerts}
                    notifyingAlerts={notifyingAlerts}
                    onResolve={onResolve}
                    onDismiss={onDismiss}
                    onNotify={onNotify}
                    onRowClick={onAlertClick}
                    hasTestData={hasTestData}
                    hasNotificationChannels={hasNotificationChannels}
                    onClearAllFilters={onAlertFiltersClearAll}
                    onPopulateTestData={onPopulateTestData}
                    isGeneratingTestData={isGeneratingTestData}
                  />
                </div>

                {/* Monitored Products Tab */}
                <div className={`tab-content ${selectedTab === 1 ? 'tab-active' : ''}`}>
                  <MonitoredProductsTabContent
                    filteredProducts={filteredProducts}
                    searchQuery={searchQuery}
                    onboardingData={onboardingData}
                    shopDomain={shopDomain}
                    onProductClick={onProductClick}
                    hasTestData={hasTestData}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    itemsPerPage={itemsPerPage}
                    onPageChange={onPageChange}
                    onOpenWizard={onOpenWizard}
                  />
                </div>

                {/* Alert History Tab */}
                <div className={`tab-content ${selectedTab === 2 ? 'tab-active' : ''}`}>
                  <AlertHistoryTabContent
                    filteredHistory={filteredHistory}
                    searchQuery={searchQuery}
                    hasTestData={hasTestData}
                  />
                </div>
              </div>
            </AsyncSection>
          </Box>
        </BlockStack>
      </Card>
    </div>
  );
}
